import { showSnackbar, snackbarTypes } from 'src/redux/snackbar'
import { fetchDispatch } from 'data/helpers'
import { fetchSSE } from 'data/sseHelpers'

// Action Types
export const SEND_MESSAGE_LOADING = 'aiJobs/SEND_MESSAGE_LOADING'
export const SEND_MESSAGE_SUCCESS = 'aiJobs/SEND_MESSAGE_SUCCESS'
export const SEND_MESSAGE_FAILURE = 'aiJobs/SEND_MESSAGE_FAILURE'

export const UPDATE_AI_JOB_FAVORITE = 'aiJobs/UPDATE_AI_JOB_FAVORITE'

export const ADD_CHAT_MESSAGE = 'aiJobs/ADD_CHAT_MESSAGE'
export const CLEAR_CHAT_HISTORY = 'aiJobs/CLEAR_CHAT_HISTORY'
export const SET_AI_TYPING = 'aiJobs/SET_AI_TYPING'
export const RESET_CONVERSATION = 'aiJobs/RESET_CONVERSATION'
export const SET_MESSAGE_FEEDBACK = 'aiJobs/SET_MESSAGE_FEEDBACK'
export const SET_JOB_FEEDBACK = 'aiJobs/SET_JOB_FEEDBACK'
export const SET_MOBILE_DRAWER_STATE = 'aiJobs/SET_MOBILE_DRAWER_STATE'
export const SET_ACTIVE_SEARCH_SOURCE = 'aiJobs/SET_ACTIVE_SEARCH_SOURCE'
export const ADD_SYSTEM_MESSAGE = 'aiJobs/ADD_SYSTEM_MESSAGE'
export const SET_PRESERVE_JOB_RESULTS = 'aiJobs/SET_PRESERVE_JOB_RESULTS'
export const SET_ACTIVE_JOB_CONTEXT = 'aiJobs/SET_ACTIVE_JOB_CONTEXT'
export const UPDATE_JOB_LISTING_ARGUMENTS = 'aiJobs/UPDATE_JOB_LISTING_ARGUMENTS'
export const SET_TYPING_STATUS = 'aiJobs/SET_TYPING_STATUS'

// Action Creators
export const sendMessageLoading = () => ({ type: SEND_MESSAGE_LOADING })
export const sendMessageSuccess = payload => ({ type: SEND_MESSAGE_SUCCESS, payload })
export const sendMessageFailure = payload => ({ type: SEND_MESSAGE_FAILURE, payload })

export const updateAiJobFavorite = payload => ({ type: UPDATE_AI_JOB_FAVORITE, payload })

export const addChatMessage = payload => ({ type: ADD_CHAT_MESSAGE, payload })
export const clearChatHistory = () => ({ type: CLEAR_CHAT_HISTORY })
export const setAiTyping = payload => ({ type: SET_AI_TYPING, payload })
export const resetConversation = () => ({ type: RESET_CONVERSATION })
export const setMessageFeedback = payload => ({ type: SET_MESSAGE_FEEDBACK, payload })
export const setJobFeedback = payload => ({ type: SET_JOB_FEEDBACK, payload })
export const setMobileDrawerState = payload => ({ type: SET_MOBILE_DRAWER_STATE, payload })
export const setActiveSearchSource = payload => ({ type: SET_ACTIVE_SEARCH_SOURCE, payload })
export const addSystemMessage = payload => ({ type: ADD_SYSTEM_MESSAGE, payload })
export const setPreserveJobResults = payload => ({ type: SET_PRESERVE_JOB_RESULTS, payload })
export const setActiveJobContext = payload => ({ type: SET_ACTIVE_JOB_CONTEXT, payload })
export const updateJobListingArguments = payload => ({ type: UPDATE_JOB_LISTING_ARGUMENTS, payload })
export const setTypingStatus = payload => ({ type: SET_TYPING_STATUS, payload })

export const CHAT_RESPONSE_TIMEOUT_MS = 45000

let activeAbortController = null

const handleChatTimeout = (dispatch) => {
  activeAbortController = null
  dispatch(addChatMessage({
    type: 'ai',
    message: "Sorry, that took too long. Please try again!",
    metadata: { isSystem: true }
  }))
  dispatch(sendMessageFailure(null))
  dispatch(setTypingStatus(null))
  dispatch(setAiTyping(false))
}

const handleChatResponse = (dispatch, response) => {
  activeAbortController = null
  dispatch(sendMessageSuccess(response))

  if (response.content) {
    dispatch(addChatMessage({
      type: 'ai',
      message: response.content,
      metadata: {
        chatbotMessageId: response.chatbotMessageId,
        jobsCount: response.rankedJobs?.length || 0,
      }
    }))
  }

  dispatch(setTypingStatus(null))
  dispatch(setAiTyping(false))
}

const handleChatError = (dispatch, error) => {
  activeAbortController = null
  dispatch(
    showSnackbar(
      'Sorry! We encountered an error. Please try again.',
      snackbarTypes.ERROR,
    ),
  )
  dispatch(sendMessageFailure(error?.response))
  dispatch(setTypingStatus(null))
  dispatch(setAiTyping(false))
}

const sendViaSSE = (dispatch, message, conversationId) => {
  const abortController = new AbortController()

  if (activeAbortController) {
    activeAbortController.abort()
  }
  activeAbortController = abortController
  let handled = false

  let timeoutId = setTimeout(() => {
    if (handled) return
    handled = true
    abortController.abort()
    handleChatTimeout(dispatch)
  }, CHAT_RESPONSE_TIMEOUT_MS)

  return new Promise((resolve, reject) => {
    const { promise } = fetchSSE({
      path: '/api/job_chatbot/message/stream',
      values: { content: message, conversationId },
      abortController,
      onProgress: (data) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          if (handled) return
          handled = true
          abortController.abort()
          handleChatTimeout(dispatch)
        }, CHAT_RESPONSE_TIMEOUT_MS)
        dispatch(setTypingStatus(data))
      },
      onComplete: (data) => {
        if (handled) return
        handled = true
        clearTimeout(timeoutId)
        handleChatResponse(dispatch, data)
        resolve()
      },
      onError: (data) => {
        if (handled) return
        handled = true
        clearTimeout(timeoutId)
        handleChatError(dispatch, new Error(data?.message || 'Stream error'))
        resolve()
      },
    })
    promise.catch(error => {
      clearTimeout(timeoutId)
      reject(error)
    })
  })
}

const sendViaFetch = async (dispatch, message, conversationId) => {
  const abortController = new AbortController()
  const timeoutId = setTimeout(() => {
    abortController.abort()
    handleChatTimeout(dispatch)
  }, CHAT_RESPONSE_TIMEOUT_MS)

  const response = await fetchDispatch({
    path: '/api/job_chatbot/message',
    method: 'POST',
    values: { content: message, conversationId },
    signal: abortController.signal,
  })
  clearTimeout(timeoutId)
  handleChatResponse(dispatch, response)
}

// Thunk Actions
export const sendChatMessage = ({ message, conversationId }) => {
  return async dispatch => {
    dispatch(sendMessageLoading())
    dispatch(setAiTyping(true))
    dispatch(setTypingStatus(null))

    try {
      await sendViaSSE(dispatch, message, conversationId)
    } catch (sseError) {
      if (sseError?.name === 'AbortError') return
      try {
        await sendViaFetch(dispatch, message, conversationId)
      } catch (error) {
        if (error?.name === 'AbortError') return
        handleChatError(dispatch, error)
      }
    }
  }
}

export const stopChatRequest = () => {
  return dispatch => {
    if (activeAbortController) {
      activeAbortController.abort()
      activeAbortController = null
    }
    dispatch(setTypingStatus(null))
    dispatch(setAiTyping(false))
    dispatch(sendMessageFailure(null))
    dispatch(addChatMessage({
      type: 'ai',
      message: 'Request stopped.',
      metadata: { isSystem: true }
    }))
  }
}

export const clearConversation = () => {
  return dispatch => {
    if (activeAbortController) {
      activeAbortController.abort()
      activeAbortController = null
    }
    dispatch(resetConversation())
  }
}

const FEEDBACK_REASON_VALUES = {
  didnt_understand: 1,
  wrong_specialty: 2,
  wrong_location: 3,
  salary_too_low: 4,
  irrelevant_jobs: 5,
  missing_criteria: 6,
  inaccurate: 7,
  not_helpful: 8,
  relevant_jobs: 9,
  right_specialty: 10,
  good_location: 11,
  fair_salary: 12,
  understood_needs: 13,
  helpful_response: 14,
  other: 15,
}

const VOTE_VALUES = { up: 1, down: 0 }

export const submitMessageFeedback = ({ chatbotMessageId, conversationId, vote, reasons, comment }) => {
  fetchDispatch({
    path: '/api/job_chatbot/feedback',
    values: {
      conversationId,
      chatbotMessageId,
      vote: VOTE_VALUES[vote],
      messageReasons: reasons.map(r => FEEDBACK_REASON_VALUES[r]).filter(Boolean),
      comment,
    },
    method: 'POST',
  }).catch(() => {})
}

const JOB_FEEDBACK_REASON_VALUES = {
  inaccurate_match: 1,
  wrong_specialty: 2,
  wrong_location: 3,
  salary_mismatch: 4,
  missing_requirements: 5,
  justification_unclear: 6,
  accurate_match: 7,
  good_specialty_fit: 8,
  location_right: 9,
  salary_meets_expectations: 10,
  criteria_match: 11,
  other: 12,
}

export const submitJobFeedback = ({ jobId, conversationId, chatbotMessageId, vote, reasons, comment }) => {
  fetchDispatch({
    path: '/api/job_chatbot/job_feedback',
    values: {
      conversationId,
      chatbotMessageId,
      jobId,
      vote: VOTE_VALUES[vote],
      jobReasons: reasons.map(r => JOB_FEEDBACK_REASON_VALUES[r]).filter(Boolean),
      comment,
    },
    method: 'POST',
  }).catch(() => {})
}

// Initial State
const initialState = {
  loading: false,
  conversationId: null,
  rankedJobs: [],
  jobListingArguments: null,
  resolvedFilters: null,
  chatHistory: [],
  isAiTyping: false,
  mobileDrawerState: 'minimized', // 'full' | 'minimized' | 'expanded' | 'hidden'
  activeSearchSource: 'traditional', // 'traditional' | 'ai'
  preserveJobResults: false,
  activeJobContext: null,
  suggestedFollowUps: [],
  typingStatus: null,
}

// Reducer
export const aiJobSearchReducer = (state = initialState, action) => {
  switch (action.type) {
    case SEND_MESSAGE_LOADING:
      return { ...state, loading: true }

    case SEND_MESSAGE_SUCCESS: {
      const hasNewJobs = action.payload.rankedJobs != null
      const shouldPreserveJobs = state.preserveJobResults && hasNewJobs
      return {
        ...state,
        loading: false,
        conversationId: action.payload.conversationId || state.conversationId,
        rankedJobs: hasNewJobs && !shouldPreserveJobs
          ? action.payload.rankedJobs.map(rj => ({ ...rj, chatbotMessageId: action.payload.chatbotMessageId }))
          : state.rankedJobs,
        jobListingArguments: !shouldPreserveJobs
          ? (action.payload.jobListingArguments ?? state.jobListingArguments)
          : state.jobListingArguments,
        resolvedFilters: !shouldPreserveJobs
          ? (action.payload.resolvedFilters ?? state.resolvedFilters)
          : state.resolvedFilters,
        suggestedFollowUps: action.payload.suggestedFollowUps || [],
        activeSearchSource: hasNewJobs && !shouldPreserveJobs ? 'ai' : state.activeSearchSource,
        preserveJobResults: false,
        typingStatus: null,
      }
    }

    case SEND_MESSAGE_FAILURE:
      return { ...state, loading: false, typingStatus: null }

    case UPDATE_AI_JOB_FAVORITE:
      return {
        ...state,
        rankedJobs: state.rankedJobs.map(rankedJob =>
          rankedJob.job?.id === action.payload.jobId
            ? {
                ...rankedJob,
                job: {
                  ...rankedJob.job,
                  candidateJob: {
                    ...rankedJob.job.candidateJob,
                    favorite: action.payload.favorite,
                  },
                },
              }
            : rankedJob
        ),
      }

    case SET_MESSAGE_FEEDBACK:
      return {
        ...state,
        chatHistory: state.chatHistory.map(msg =>
          msg.id === action.payload.messageId
            ? { ...msg, feedback: { ...action.payload } }
            : msg
        ),
      }

    case SET_JOB_FEEDBACK:
      return {
        ...state,
        rankedJobs: state.rankedJobs.map(rankedJob =>
          rankedJob.job?.id === action.payload.jobId
            ? { ...rankedJob, feedback: { ...action.payload } }
            : rankedJob
        ),
      }

    case ADD_CHAT_MESSAGE:
      return {
        ...state,
        chatHistory: [...state.chatHistory, {
          id: Date.now() + Math.random(),
          type: action.payload.type,
          message: action.payload.message,
          timestamp: new Date().toISOString(),
          ...action.payload.metadata
        }]
      }

    case CLEAR_CHAT_HISTORY:
      return {
        ...state,
        chatHistory: []
      }

    case SET_AI_TYPING:
      return {
        ...state,
        isAiTyping: action.payload
      }

    case RESET_CONVERSATION:
      return {
        ...initialState,
        mobileDrawerState: state.mobileDrawerState,
        activeSearchSource: 'traditional',
      }

    case SET_MOBILE_DRAWER_STATE:
      return {
        ...state,
        mobileDrawerState: action.payload
      }

    case SET_ACTIVE_SEARCH_SOURCE:
      return {
        ...state,
        activeSearchSource: action.payload,
      }

    case ADD_SYSTEM_MESSAGE:
      return {
        ...state,
        chatHistory: [...state.chatHistory, {
          id: Date.now() + Math.random(),
          type: 'system',
          isSystem: true,
          message: action.payload,
          timestamp: new Date().toISOString(),
        }],
      }

    case SET_PRESERVE_JOB_RESULTS:
      return {
        ...state,
        preserveJobResults: action.payload,
      }

    case SET_ACTIVE_JOB_CONTEXT:
      return {
        ...state,
        activeJobContext: action.payload,
      }

    case UPDATE_JOB_LISTING_ARGUMENTS:
      return {
        ...state,
        jobListingArguments: action.payload,
      }

    case SET_TYPING_STATUS:
      return {
        ...state,
        typingStatus: action.payload,
      }

    default:
      return state
  }
}
